import { GenericDialog, IGenericDialogButton } from '@AMIEWEB/Alerts/GenericDialog';
import { Grid, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlacementDocuments } from 'store/redux-store/placement-documents/selector';
import { placementDocumentsActions } from 'store/redux-store/placement-documents/slice';
import {
  ClientConfirmationSteps,
  ConfirmationLetterSubjectPrefix,
  ConfirmationLetterTemplates,
  DocStatus,
  IPlacementClientConfirmationDoc,
  KeyContactsDescription,
  PlacementDocumentSource,
} from 'store/redux-store/placement-documents/types';
import { makeStyles } from 'tss-react/mui';
import { ClientConfirmationDialogContent } from './ClientConfirmationDialogContent';
import {
  selectComparisonData,
  selectMiscellaneousPlacementData,
  selectPlacementCandidateRecords,
  selectPlacementContactsData,
  selectPlacementHeaderData,
} from 'store/redux-store/placement-details/selectors';
import { useNavigate, useLocation } from 'react-router-dom';
import { redirectToClientConfirmationDocEditor } from '@AMIEWEB/Placement/CreateClientConfirmationDoc/utils';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { getCandidateById, getEMRList } from 'app/services/CandidateServices/CandidateServices';
import { getOrderDetailsById } from 'app/services/OrderServices/OrderServices';
import { applyDataToTemplate, GetTemplatesByCategory } from 'app/services/NotificationServices/TemplateService';
import { getCandidateCredentials } from 'app/services/CredentialingServices';
import { getCandidateSellingPointsByPid } from 'app/services/PlacementServices/PlacementServices';
import { getCredentialLookups } from 'app/services/CredentialingServices/CredentialingServices';
import { RequirementType } from 'app/models/Credentialing/enums/RequirementType';
import { orderBy } from 'lodash';
import { LookupFields } from 'app/models/Credentialing/enums/Lookup';
import moment from 'moment';
import { selectPlacementSummary, selectSubmissionInfo } from 'store/redux-store/placement-summary/selectors';
import { GetMomentOfDate } from 'utils/dates/moment';
import { Concatenate } from 'utils/string/string';
import { notificationSelection } from 'store/redux-store/notification/notification.selector';
import { selectUser } from 'oidc/user.selectors';
import { placementContactsFlattened } from '@AMIEWEB/Placement/PlacementDetails/PlacementDetailsHeader/utils';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { ContactProps } from 'app/models/Notification/Notification';
import { httpSuccess } from 'app/services/serviceHelpers';
import { base64DocumentToURL } from 'app/helpers/fileHelper';
import { getSubstitutions } from '@AMIEWEB/Placement/PlacementDetails/helper';
import { signatureSubstitutionToken } from '@AMIEWEB/GlobalSearch/helper';
import { ChannelType, FormName, SendType, UseSubType, UseType } from '@AMIEWEB/Notification/Constants';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import { EmailDefaultValues } from 'app/models/Email/IEmail';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { CustomBackdrop } from '@AMIEWEB/Common';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import { OrderTypeIds } from 'app/models/Placement/PlacementDetails';
import {
  getExpirationDate,
  getFluVaccinationStatus,
  getUniqueArray,
} from '@AMIEWEB/Common/TemplateEditor/ClientConfirmation/helper';

const useStyles = makeStyles()({
  dialogContainer: {
    width: 670,
    maxWidth: 670,
  },
  dialogConfirmationContainer: {
    width: '500px',
    maxWidth: '500px',
  },
});
const dialogStyle = currentStep => {
  switch (currentStep) {
    case ClientConfirmationSteps.initial:
      return 'blue';
    case ClientConfirmationSteps.download:
    case ClientConfirmationSteps.sendNow:
      return 'white';
    default:
      return 'blue';
  }
};

export const ClientConfirmationDialog = ({ open, error, loading, onClose, ...props }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const history = useNavigate();
  const location = useLocation();

  const placementDetails = useSelector(selectPlacementHeaderData);
  const {
    clientConfirmationDocs,
    selectedConfirmationDoc,
    miscellaneous: { currentClientConfirmationStep: currentStep, errorProps },
  } = useSelector(selectPlacementDocuments);

  const {
    orderReq: {
      candidate: { experience: candidateReqItem, certifications, licenses },
    },
  } = useSelector(selectSubmissionInfo);
  const { candidate: candidateDetails } = useSelector(selectComparisonData);
  const { candidateSellingPointCounts } = useSelector(selectPlacementCandidateRecords);
  const notificationData = useSelector(notificationSelection);
  const { userInfo } = useSelector(selectUser);
  const placementContacts = useSelector(selectPlacementContactsData);
  const { disciplineData } = useSelector(selectMiscellaneousPlacementData);
  const placementSummary = useSelector(selectPlacementSummary);
  const flattenedPlacementDetails = placementContactsFlattened(placementContacts);
  const userPreference = useSelector(selectUserPreference);
  const user = useSelector(selectUser);

  const { promiseInProgress: loadingDocs } = usePromiseTracker({
    area: 'get-placement-client-confirmation-documents',
    delay: 0,
  });

  const { promiseInProgress: downloading } = usePromiseTracker({
    area: 'download-placement-client-confirmation-documents',
    delay: 0,
  });

  const { promiseInProgress: updatingSentingStatus } = usePromiseTracker({
    area: 'update-confirmation-doc-status-to-sent',
    delay: 0,
  });

  const dispatch = useDispatch();

  const [loader, setLoader] = React.useState<boolean>(false);

  const setCurrentStep = (step: ClientConfirmationSteps) =>
    dispatch(placementDocumentsActions.setMiscellaneousField({ key: 'currentClientConfirmationStep', data: step }));

  const onDocSelection = React.useCallback((doc: IPlacementClientConfirmationDoc) => {
    dispatch(placementDocumentsActions.setSelectedClientConfirmationDoc(doc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialogTitle = React.useCallback((step: ClientConfirmationSteps) => {
    switch (step) {
      case ClientConfirmationSteps.initial:
        return t('placement.profile.clientConfirmation.dialog.title.initial');
      case ClientConfirmationSteps.download:
        return t('placement.profile.clientConfirmation.dialog.title.download');
      case ClientConfirmationSteps.sendNow:
        return t('placement.profile.clientConfirmation.dialog.title.sendNow');
      default:
        return t('placement.profile.clientConfirmation.dialog.title.initial');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = React.useCallback((doc: IPlacementClientConfirmationDoc) => {
    if (doc.status === DocStatus.Draft) {
      /** Code: Close client confirmation dialog */
      dispatch(placementStatusAction.setSelectedStatus({ status: undefined }));
      redirectToClientConfirmationDocEditor(history, location, { defaultSelection: 'draft' });
      return;
    }
    Object.assign(document.createElement('a'), {
      target: '_blank',
      rel: 'noopener noreferrer',
      href:
        doc.source === PlacementDocumentSource.classic
          ? `/placement/document/confirmation/${doc.documentImageId}`
          : `/placement/document/${doc.documentImageId}`,
    }).click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotificationData = async () => {
    const sharedService = SharedService.createInstance();
    setLoader(true);
    try {
      const [
        selectedConfirmationData,
        candidateData,
        candidateCreds,
        candidateEmrList,
        orderDetails,
        sellingPoints,
        lookupselection,
        templates,
      ] = await Promise.all([
        selectedConfirmationDoc.source === PlacementDocumentSource.web
          ? sharedService.getPlacementDocument(selectedConfirmationDoc.documentId)
          : sharedService.getPlacementClassicDocument(
              'confirmation',
              parseInt(selectedConfirmationDoc.documentImageId),
            ),
        getCandidateById(placementDetails.candidate.id, placementDetails.brandId),
        getCandidateCredentials(placementDetails.candidate.id.toString()),
        getEMRList(placementDetails.candidate.id, placementDetails.brandId),
        getOrderDetailsById(`${placementDetails.orderId}`),
        getCandidateSellingPointsByPid(placementDetails.placementId),
        getCredentialLookups('all'),
        GetTemplatesByCategory('Confirmation Letter', 'email'),
      ]);

      const candidateEmr = candidateEmrList?.data
        .filter(item => item?.candidateId == placementDetails.candidate.id && item.brandId == placementDetails.brandId)
        ?.map(item => item?.description);
      const ssn = candidateData?.socialSecurityNumber?.replace(/\d/g, '#');
      const skillSets = candidateData?.verifiedSkills?.map(item => `${item?.disciplineAbbr}-${item?.specialtyAbbr}`);
      const fluData = candidateCreds.employeeHealth?.filter(
        e => e.requirementTypeID === RequirementType.FLUVACCINATION,
      );
      const firstReport = orderBy(fluData, ['dateCompleted'], ['desc'])[0];
      const type = lookupselection
        ?.filter(e => e.requirementTypeID === RequirementType.FLUVACCINATION)
        ?.find(item => item.lookUpField === LookupFields.TypeID)
        ?.lookups?.find(e => e.value === firstReport?.typeID);

      const expirationDate = getExpirationDate(firstReport?.dateCompleted);

      const CandidateFludata = {
        type: type?.description,
        dateCompleted: firstReport?.dateCompleted,
        expirationDate: expirationDate,
        tsceFormID: firstReport?.tsceFormID,
      };
      const activeLicences: Array<string> = [];
      licenses?.data?.map(item => {
        const expirationDate =
          GetMomentOfDate(item.expirationDate).format('MM/DD/YYYY') !== 'Invalid date'
            ? GetMomentOfDate(item.expirationDate).format('MM/DD/YYYY')
            : undefined;
        if (moment(expirationDate).isSameOrAfter(moment(), 'day')) {
          return activeLicences.push(
            `${item?.requirementType || ''} ${item.state} ${item?.level == 'Multi-state' ? 'Compact' : ''}${
              item?.licenseNumber ? '#' + item?.licenseNumber : ''
            }${expirationDate ? ' (exp ' + expirationDate + ')' : ''}`,
          );
        }
      });
      const licenseNumbers: Array<string> = [];
      candidateCreds?.licenses?.map(item => {
        if (!!item?.licenseNumber) {
          return licenseNumbers.push(`${item?.licenseNumber}`);
        }
      });
      const activeCertifications: Array<string> = [];
      candidateCreds?.certifications?.map(item => {
        const expirationDate =
          GetMomentOfDate(item.expirationDate).format('MM/DD/YYYY') !== 'Invalid date'
            ? GetMomentOfDate(item.expirationDate).format('MM/DD/YYYY')
            : undefined;
        if (moment(expirationDate).isSameOrAfter(moment(), 'day')) {
          return activeCertifications.push(
            `${item.requirementType || ''}${expirationDate ? ' (exp ' + expirationDate + ')' : ''}`,
          );
        }
      });

      const uniqueLicences = getUniqueArray(activeLicences);
      const uniqueCertifications = getUniqueArray(activeCertifications);
      const fluVaccinationStatus = getFluVaccinationStatus(CandidateFludata);
      const vmsReqNumber = placementDetails?.vmsReqDetails?.vmsReqNo;
      const vmsName = placementDetails?.technologyVendorName;
      const vmsDetails = vmsName && vmsName !== '' ? ` - ${vmsName}${vmsReqNumber ? `: ${vmsReqNumber}` : ''}` : '';
      const orderSkillSets = [];
      placementSummary.submissionInfo.orderReq.order.skillset.data.map(item => {
        item?.specialties.map(ele => {
          orderSkillSets.push(`${item?.disciplineAbbr || ''}-${ele?.specialtyAbbr || ''}`);
        });
      });
      const values = {
        facilityUnit: placementDetails?.placementUnit,
        accountManagerFirstName: flattenedPlacementDetails?.accountManager?.firstName,
        accountManagerLastName: flattenedPlacementDetails?.accountManager?.lastName,
        accountManagerEmailAddress: flattenedPlacementDetails?.accountManager?.email,
        accountManagerPhoneNumber: flattenedPlacementDetails?.accountManager?.phoneNumber,
        placementSellingPoints: `${sellingPoints?.placementSellingPoints?.replaceAll('\n', '<br/>') || ''}`,
        candidateFirstName: placementDetails.candidate?.firstName,
        candidateLastName: placementDetails.candidate?.lastName,
        candidateGoesBy: placementDetails.candidate?.goesByName,
        candidateDiscipline: candidateDetails?.skillsets,
        candidateDob:
          (candidateData?.dateOfBirth && GetMomentOfDate(candidateData?.dateOfBirth).format('MM/DD/YYYY')) || '',
        currentAMNTraveler: candidateSellingPointCounts?.currentAmnTraveler,
        candidateDisciplineText: disciplineData,
        candidatePhoneNumber: placementDetails.candidate?.phoneNumber?.[0]?.phoneNumber,
        candidateEmail: placementDetails.candidatePrimaryEmail,
        availableStartDate: placementDetails.availabilityDate,
        requestedTimeOff: candidateDetails?.requestedTimeOff,
        licenses: licenses?.data,
        certifications: certifications.data?.candidateCredCertListResponse,
        totalYearsofExperience: `${
          candidateData?.experience?.totalYearsOfExp ? candidateData?.experience?.totalYearsOfExp?.toString() + 'Y' : ''
        } ${
          candidateData?.experience?.totalMonthsOfExperience
            ? candidateData?.experience?.totalMonthsOfExperience?.toString() + 'M'
            : ''
        }`,
        COVIDVaccineStatus: candidateData.covidVaccinationstatus,
        experience: candidateReqItem,
        orderShift: orderDetails.shifts?.[0]?.description || '',
        orderGww: orderDetails.gww && orderDetails.gww == true ? 'Yes' : 'No',
        ssn: ssn,
        fluVaccinationStatus: fluVaccinationStatus,
        candidateEmr: candidateEmr,
        assignmentDuration: placementSummary.orderInformation.numberOfWeeks
          ? `${placementSummary.orderInformation.numberOfWeeks} weeks`
          : null,
        vmsReqNumber: vmsReqNumber,
        candidateSkillSets: skillSets,
        orderSkillSets: orderSkillSets,
        activeCertifications: uniqueCertifications.join(', '),
        activeLicences: uniqueLicences.join(', '),
        placementStartDate: placementDetails.placementStartDate,
        placementEndDate: placementDetails.placementEndDate,
        candidateId: `${placementDetails?.candidate?.id}`,
        placementId: `${placementDetails?.placementId}`,
        licenseNumber: placementDetails.candidateLicenseNumber,
      };
      const substitutions = getSubstitutions(values);
      let fileData;

      const sender = {
        name: Concatenate([userInfo?.firstName, userInfo?.lastName], ' '),
        email: userInfo?.email,
        userId: `${userInfo?.employeeId}`,
        NtUserName: userInfo?.ntUserName,
        senderId: '',
      };
      const title =
        placementDetails.orderTypeId === OrderTypeIds.FEMA
          ? ConfirmationLetterTemplates.FEMA
          : ConfirmationLetterTemplates.Standard;
      const template = templates.filter(data => data.title == title);
      const accountManager: ContactProps = {
        name: Concatenate(
          [
            flattenedPlacementDetails?.accountManager.firstName,
            flattenedPlacementDetails?.accountManager.middleName || '',
            flattenedPlacementDetails?.accountManager.lastName,
          ],
          ' ',
        ),
        email: flattenedPlacementDetails?.accountManager?.email,
      };

      const sendConfirmationToClientContacts = placementContacts?.facilityContacts?.filter(item => {
        return item.actions.some(action => action.description == KeyContactsDescription.SendConfirmationTo);
      });
      const keyClientContactsTos: Array<ContactProps> = sendConfirmationToClientContacts?.map(item => {
        const firstName = item.firstName || '';
        const lastName = item.lastName || '';
        return {
          name: Concatenate([firstName, lastName], ' '),
          email: item?.email != '' ? item?.email : null,
          contactId: item?.id,
        };
      });
      const tos = [...keyClientContactsTos];
      if (httpSuccess(selectedConfirmationData?.status)) {
        const { data: document } = selectedConfirmationData;
        if (document?.fileType || document?.filebytes) {
          const letter = base64DocumentToURL(
            selectedConfirmationDoc.source === PlacementDocumentSource.web ? document.filebytes : document.data,
            {
              fileType: 'application/pdf',
              fileName: document.fileName,
              download: false,
            },
          );
          fileData = {
            blobUrl: letter.url,
            fileName: 'ConfirmationLetter',
            fileType: document?.fileType,
          };
        } else {
          dispatch(
            placementDocumentsActions.setMiscellaneousField({
              key: 'errorProps',
              data: {
                message: 'An error occurred while retrieving the confirmation letter to send. Please try again.',
                severity: 'error',
                error: true,
              },
            }),
          );
          setLoader(false);
          return;
        }
      } else {
        dispatch(
          placementDocumentsActions.setMiscellaneousField({
            key: 'errorProps',
            data: {
              message: 'An error occurred while retrieving the confirmation letter to send. Please try again.',
              severity: 'error',
              error: true,
            },
          }),
        );
        setLoader(false);
        return;
      }
      const payload = {
        body: template[0]?.body ? `${template[0]?.body}` : '',
        substitutions: substitutions.reduce((acc, item) => {
          if (item.name !== 'Signature') {
            acc[item.input.slice(2, -2)] = item.value || '';
          }
          return acc;
        }, {}),
        signatureSubstitutions: signatureSubstitutionToken(user, userPreference),
        brand: user.userInfo?.companyBrandName,
        id: null,
        skipSignatureSubstitution: true,
      };
      const body = await applyDataToTemplate(payload);
      const data = {
        useType: UseType.Placement,
        useSubType: UseSubType.ConfirmationLetter,
        channel: ChannelType.email,
        emailType: SendType.one_to_one,
        selectedTemplate:
          template.length > 0
            ? template[0]
            : {
                title: '',
                id: '',
                body: '',
                description: '',
                category: '',
                isPrivate: false,
                substitutions: [],
              },
        selectedPublicCategory: 'Confirmation Letter',
        publicTemplates: templates?.filter(template => template?.isPrivate !== true),
        sendType: SendType.one_to_one,
        body: body ? `${body}` : '',
        sender: sender,
        substitutions: substitutions,
        tos: tos,
        ccs: flattenedPlacementDetails?.accountManager?.email ? [accountManager] : [],
        bccs: [sender],
        confirmationLetter: true,
        associatedRecords: [
          { name: 'placementId', value: `${placementDetails?.placementId}` },
          { name: 'travelerId', value: `${placementDetails?.candidate?.id}` },
          { name: 'brandId', value: `${placementDetails?.brandId}` },
          { name: 'facilityId', value: `${placementDetails?.facility?.id}` },
          {
            name: 'candidate',
            value: Concatenate([placementDetails.candidate?.firstName, placementDetails.candidate?.lastName], ' '),
          },
          { name: 'documentId', value: `${selectedConfirmationDoc.documentId}` },
          { name: 'documentImageId', value: `${selectedConfirmationDoc?.documentImageId}` },
          { name: 'publisherId', value: `${userInfo.employeeId}` },
          { name: 'publisherName', value: `${userInfo.firstName || ''} ${userInfo.lastName || ''}` },
          { name: 'regionalDirectorId', value: `${placementContacts?.regionalDirector.id}` },
        ],
        correlationId: null,
        userRole: Concatenate(userInfo?.roles || [], ','),
        formName: FormName.Placement,
        manualLog: false,
        subject:
          placementDetails.orderTypeId === OrderTypeIds.FEMA
            ? `${ConfirmationLetterSubjectPrefix.FEMA} ${placementDetails.candidate?.firstName || ''} ${
                placementDetails.candidate.lastName || ''
              } for ${placementDetails?.placementUnit}${vmsDetails}`
            : `${ConfirmationLetterSubjectPrefix.Standard} ${placementDetails.candidate?.firstName || ''} ${
                placementDetails.candidate.lastName || ''
              }, ${placementDetails?.placementUnit}${vmsDetails}`,
        attachmentData: fileData,
      };
      if (!notificationData.email.open && notificationData.email.minimized) {
        dispatch(
          notificationDataActions.setEmailInteraction({
            open: true,
            minimized: false,
          }),
        );
        dispatch(notificationDataActions.setSnackBarData({ channel: 'email', manual: false, changeWarning: true }));
      } else {
        dispatch(
          notificationDataActions.setNotificationData({
            ...notificationData,
            email: {
              data: {
                ...EmailDefaultValues(),
                ...data,
              },
              open: true,
              minimized: false,
            },
            snackbarData: null,
          }),
        );
      }
      setLoader(false);
      onClose();
    } catch (err) {
      console.log(err);
      setLoader(false);
    }
  };

  const dialogContent = React.useMemo(
    () => {
      switch (currentStep) {
        case ClientConfirmationSteps.download:
          return (
            <Typography variant="body1">
              {t('placement.profile.clientConfirmation.dialog.content.sendToYourself')}
            </Typography>
          );
        default:
          return (
            <ClientConfirmationDialogContent
              clientConfirmationDocs={clientConfirmationDocs}
              selectedConfirmationDoc={selectedConfirmationDoc}
              onDocSelection={onDocSelection}
              loading={loadingDocs}
              inlineDownload={doc => handleDownload(doc)}
            />
          );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep, clientConfirmationDocs, selectedConfirmationDoc, loadingDocs],
  );

  const errorContent = React.useMemo(
    () =>
      error
        ? {
            error,
            errorMessage: error
              ? t('placement.profile.placementStatusUpdate.packetDownloadError')
              : t('placement.profile.placementStatusUpdate.error'),
          }
        : errorProps
        ? {
            error: true,
            errorMessage: errorProps.message,
            errorSeverity: errorProps.severity,
          }
        : undefined,
    [error, errorProps, t],
  );

  const _onCancel = (event, reason) => {
    onClose?.(event);
  };

  const initalStepDialogButtons = React.useMemo(
    () => {
      const disableDownloadBtn =
        loadingDocs ||
        (!selectedConfirmationDoc || selectedConfirmationDoc?.status === 'Draft'
          ? true
          : selectedConfirmationDoc.source === PlacementDocumentSource.classic
          ? selectedConfirmationDoc.status !== 'Sent'
          : false);
      return [
        {
          text: t('placement.profile.clientConfirmation.dialog.buttons.cancel'),
          //@ts-ignore
          onClick: _onCancel,
          color: 'tertiary',
        },
        {
          text: t('placement.profile.clientConfirmation.dialog.buttons.download'),
          variant: 'contained',
          disabled: disableDownloadBtn,
          loading: downloading,
          onClick: () => {
            dispatch(
              placementDocumentsActions.downloadConfirmationDoc({
                /** Code: Use documentImageId for download action */
                docId: selectedConfirmationDoc.documentImageId,
                docStatus: selectedConfirmationDoc.status,
                source: selectedConfirmationDoc.source,
              }),
            );
          },
          color: 'tertiary',
        },
        {
          text: t('placement.profile.clientConfirmation.dialog.buttons.sendNow'),
          variant: 'contained',
          disabled:
            !selectedConfirmationDoc ||
            selectedConfirmationDoc?.status === 'Draft' ||
            loading ||
            !Authorized(
              [userRoles.accountManagement_Leadership, userRoles.accountManagement_TeamMember],
              user.userInfo,
            ),
          onClick: event => {
            handleNotificationData();
          },
        },
      ] as IGenericDialogButton[];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_onCancel, downloading, loadingDocs, selectedConfirmationDoc],
  );

  const downloadButtons = React.useMemo(
    () => [
      {
        text: t('placement.profile.submitPacket.dialog.buttons.notNow'),
        onClick: event => {
          setCurrentStep(ClientConfirmationSteps.initial);
        },
        color: 'tertiary',
      },
      {
        text: t('placement.profile.placementStatusUpdate.markAsSent'),
        variant: 'contained',
        loading: updatingSentingStatus,
        onClick: event => {
          dispatch(
            placementDocumentsActions.updateClientConfirmationDocStatus({
              placementId: placementDetails?.placementId,
              documentId: selectedConfirmationDoc?.documentId,
            }),
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placementDetails?.placementId, selectedConfirmationDoc?.documentId, updatingSentingStatus],
  );

  const dialogButtons = (): IGenericDialogButton[] => {
    switch (currentStep) {
      case ClientConfirmationSteps.initial:
        return initalStepDialogButtons;
      case ClientConfirmationSteps.download:
        return downloadButtons;
      default:
        return [];
    }
  };

  React.useEffect(() => {
    if (open && !!placementDetails?.placementId && placementDetails?.placementId > 0) {
      dispatch(placementDocumentsActions.getClientConfirmationDocs({ placementId: placementDetails?.placementId }));
    } else dispatch(placementDocumentsActions.resetClientConfirmationDocs());
    return () => {
      dispatch(placementDocumentsActions.resetClientConfirmationDocs());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placementDetails?.placementId]);

  return (
    <GenericDialog
      open={open}
      variant={dialogStyle(currentStep)}
      onClose={_onCancel}
      classes={{
        paper: cx({
          [classes.dialogContainer]: currentStep === ClientConfirmationSteps.initial,
          [classes.dialogConfirmationContainer]: currentStep !== ClientConfirmationSteps.initial,
        }),
      }}
      dialogTitleProps={{
        text: dialogTitle(currentStep),
        closeButton: true,
      }}
      dialogActions={dialogButtons()}
      disableEscapeKeyDown={currentStep !== ClientConfirmationSteps.initial}
      dialogContentProps={{ ...errorContent }}
      {...props}
    >
      <Grid container spacing={3}>
        {loader && <CustomBackdrop open />}
        <Grid item xs={12}>
          {dialogContent}
        </Grid>
      </Grid>
    </GenericDialog>
  );
};
